/**
 * Pick WHICH result-grid columns are editable, and attach an optional 🔍
 * reference-table lookup to each — independent of persist mode.
 *
 * In the source gen-process app this lived only in the target-table column grid
 * (TargetMapping). twasta also persists to TRANSACTION objects, which have no
 * target_columns grid, so editability was undefinable there. This control hosts
 * it in the Preferences tab for ALL modes: ticking a column adds its (lower-
 * cased) name to `editableColumns`; the 🔍 button stores a lookup in
 * `editable_lookups[col]`. The engine resolves a cell's lookup from
 * target_columns first, then editable_lookups.
 */

import { useMemo, useState } from 'react'
import { Plus, Search, Zap } from 'lucide-react'
import type { ColumnLookup, ItemChange } from './types'
import { hintStyle, inputStyle } from './ui'
import LookupConfigDialog from './vendor/LookupConfigDialog'
import ItemChangeField from './ItemChangeField'

interface Props {
  /** Candidate columns (preview/source + calc + mapped transaction sources). */
  candidates: string[]
  /** Currently-editable column names (lower-cased). */
  editableColumns: string[]
  /** Per-column lookups (lower-cased keys). */
  lookups: Record<string, ColumnLookup>
  onChangeEditable: (next: string[]) => void
  onChangeLookups: (next: Record<string, ColumnLookup>) => void
  /** Per-column item_change rules (lower-cased keys). */
  itemChanges?: Record<string, ItemChange>
  onChangeItemChanges?: (next: Record<string, ItemChange>) => void
  /** Criteria names ({header.X} tokens) + project, for the item_change editors. */
  headerColumns?: string[]
  project?: string
  onOpenCode?: (method: string) => void
}

export default function EditableColumnsField({
  candidates, editableColumns, lookups, onChangeEditable, onChangeLookups,
  itemChanges = {}, onChangeItemChanges, headerColumns = [], project = '',
  onOpenCode,
}: Props) {
  const [lookupCol, setLookupCol] = useState<string | null>(null)
  const [icCol, setIcCol] = useState<string | null>(null)
  const [adding, setAdding] = useState('')

  // Show every candidate plus any already-editable / already-lookup'd column
  // that isn't a known candidate yet (e.g. a raw source column added before a
  // preview ran), so nothing the process already references disappears.
  const rows = useMemo(() => {
    const set = new Set<string>()
    for (const c of candidates) set.add(c.toLowerCase())
    for (const c of editableColumns) set.add(c.toLowerCase())
    for (const k of Object.keys(lookups || {})) set.add(k.toLowerCase())
    return [...set].sort()
  }, [candidates, editableColumns, lookups])

  const isOn = (c: string) => editableColumns.includes(c.toLowerCase())

  const toggle = (c: string) => {
    const lc = c.toLowerCase()
    onChangeEditable(isOn(c)
      ? editableColumns.filter((x) => x !== lc)
      : [...editableColumns, lc])
  }

  const addManual = () => {
    const lc = adding.trim().toLowerCase()
    setAdding('')
    if (!lc || editableColumns.includes(lc)) return
    onChangeEditable([...editableColumns, lc])
  }

  const saveLookup = (col: string, lk: ColumnLookup) =>
    onChangeLookups({ ...(lookups || {}), [col]: lk })
  const clearLookup = (col: string) => {
    const next = { ...(lookups || {}) }
    delete next[col]
    onChangeLookups(next)
  }

  const saveItemChange = (col: string, ic: ItemChange) =>
    onChangeItemChanges?.({ ...(itemChanges || {}), [col]: ic })
  const clearItemChange = (col: string) => {
    const next = { ...(itemChanges || {}) }
    delete next[col]
    onChangeItemChanges?.(next)
  }

  return (
    <div style={{ marginTop: 8 }}>
      {rows.length === 0 ? (
        <div style={hintStyle}>
          No columns known yet — run a preview on the <strong>Review &amp;
          Execute</strong> tab, or type a column name below to mark it editable.
        </div>
      ) : (
        <div style={{
          border: '1px solid var(--color-border)', borderRadius: 6,
          maxHeight: 240, overflowY: 'auto',
        }}>
          {rows.map((c) => {
            const on = isOn(c)
            const lk = (lookups || {})[c]
            return (
              <div key={c} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px',
                borderBottom: '1px solid var(--color-border)',
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, cursor: 'pointer', fontSize: 13.5 }}>
                  <input type="checkbox" checked={on} onChange={() => toggle(c)} />
                  <span style={{ fontFamily: 'monospace' }}>{c}</span>
                </label>
                {lk && (
                  <span style={{ ...hintStyle, marginTop: 0, fontFamily: 'monospace' }}>
                    🔍 {lk.table}({lk.value_column})
                  </span>
                )}
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ padding: '2px 6px' }}
                  disabled={!on}
                  title={on
                    ? (lk ? 'Edit this column’s lookup' : 'Add a reference-table lookup')
                    : 'Tick the column first'}
                  onClick={() => setLookupCol(c)}
                >
                  <Search size={13} style={{ color: lk ? 'var(--color-accent)' : undefined }} />
                </button>
                {onChangeItemChanges && (
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ padding: '2px 6px' }}
                    disabled={!on}
                    title={on
                      ? 'item_change: when this column changes, set other columns'
                      : 'Tick the column first'}
                    onClick={() => setIcCol(icCol === c ? null : c)}
                  >
                    <Zap size={13} style={{ color: (itemChanges || {})[c] ? 'var(--color-accent)' : undefined }} />
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {icCol && onChangeItemChanges && (
        <ItemChangeField
          key={icCol}
          triggerColumn={icCol}
          value={(itemChanges || {})[icCol] || null}
          formColumns={candidates}
          headerColumns={headerColumns}
          project={project}
          onChange={(ic) => saveItemChange(icCol, ic)}
          onClear={() => { clearItemChange(icCol); setIcCol(null) }}
          onOpenCode={onOpenCode}
        />
      )}

      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <input
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addManual() } }}
          placeholder="add a column by name…"
          style={{ ...inputStyle, flex: 1 }}
        />
        <button className="btn btn-secondary btn-sm" onClick={addManual} disabled={!adding.trim()}>
          <Plus size={13} /> Add
        </button>
      </div>
      <div style={hintStyle}>
        Ticked columns can be edited inline on the result grid before Execute.
        Attach a 🔍 lookup so the cell offers a searchable picker from a
        reference table.
      </div>

      {lookupCol && (
        <LookupConfigDialog
          name={lookupCol}
          value={(lookups || {})[lookupCol]}
          onClose={() => setLookupCol(null)}
          onSave={(lk) => { saveLookup(lookupCol, lk); setLookupCol(null) }}
          onClear={() => { clearLookup(lookupCol); setLookupCol(null) }}
        />
      )}
    </div>
  )
}
